import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { Button, Checkbox, Select, TextInput } from '@kcftech/design-system';
import { PropertyValueType } from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { CreateModelStep2FormData } from 'pages/Models/AnalyticsModelTab/CreateModelStep2';
import { useMemo } from 'react';
import {
    Controller,
    RegisterOptions,
    useFieldArray,
    UseFormReturn,
} from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { v4 as uuid } from 'uuid';

const labelMaxLength = 50;

const Parameter = styled.div`
    display: grid;
    grid-template-columns: 3fr 2fr 1fr auto;
    column-gap: 2rem;
    row-gap: 1rem;
    align-items: center;
`;

const parameterTypeOptions = [
    { title: 'Boolean', value: PropertyValueType.Boolean },
    { title: 'Double', value: PropertyValueType.Double },
    { title: 'Float', value: PropertyValueType.Float },
    { title: 'Integer', value: PropertyValueType.Integer },
    { title: 'String', value: PropertyValueType.String },
];

export type AnalyticsModelParameterFormData = {
    key: string;
    label: string;
    type: PropertyValueType;
    isRequired: boolean;
};

export type AnalyticsParametersInputProps = Readonly<{
    form: UseFormReturn<CreateModelStep2FormData>;
}>;

export function AnalyticsParametersInput({
    form: {
        control,
        formState: { errors, isValidating },
        register,
        getValues,
    },
}: AnalyticsParametersInputProps) {
    const {
        fields: parameters,
        append: appendParameter,
        remove: removeParameter,
    } = useFieldArray({
        control,
        name: 'parameters',
        shouldUnregister: true,
    });

    const addNewParameter = () => {
        appendParameter({
            key: uuid(),
            label: '',
            type: PropertyValueType.String,
            isRequired: false,
        });
    };

    const labelRules = useMemo<RegisterOptions>(
        () => ({
            required: 'Parameter label is required.',
            maxLength: {
                value: labelMaxLength,
                message: `Label cannot exceed ${labelMaxLength} characters.`,
            },
            validate: (value: string) => {
                const notUnique =
                    getValues().parameters.filter(({ label }) => label === value).length >
                    1;

                return notUnique ? 'Parameter label must be unique' : undefined;
            },
        }),
        [getValues]
    );

    return (
        <>
            {parameters.map((parameter, index) => (
                <Parameter key={parameter.key}>
                    <div>
                        <TextInput
                            required
                            label="Parameter Name"
                            placeholder="Enter parameter name"
                            error={errors.parameters?.[index]?.label?.message}
                            {...register(
                                `parameters.${index}.label` as const,
                                labelRules
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            control={control}
                            name={`parameters.${index}.type` as const}
                            render={({ field }) => (
                                <Select
                                    options={parameterTypeOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Type"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            control={control}
                            name={`parameters.${index}.isRequired` as const}
                            render={({ field }) => (
                                <Checkbox
                                    label="Required"
                                    checked={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <HoverIconButton
                            icon={faTrash}
                            hoverIcon={faTrashSolid}
                            color={tokens.text.secondary}
                            hoverColor={tokens.error.default}
                            size="14"
                            onClick={() => removeParameter(index)}
                            title="Remove parameter"
                        />
                    </div>
                </Parameter>
            ))}
            <div className="text-right">
                <Button type="button" onClick={addNewParameter} disabled={isValidating}>
                    Add Parameter
                </Button>
            </div>
        </>
    );
}
